import { useChecklistMediaPreview } from '@/hooks/useChecklistMediaPreview';
import { useRetainedLocalMediaPreview } from '@/hooks/useRetainedLocalMediaPreview';
import { ChecklistImageLoadNotice } from './ChecklistImageLoadNotice';
import { subscribeResumedChecklistMedia } from '@/services/checklist/ChecklistImageService';
import { showChecklistMediaFeedback } from '@/services/checklist/checklistMediaFeedback';
import { mediaErrorCode } from '@/services/diagnostics/MediaDiagnosticHistory';
import { beginMediaAttempt, finishMediaAttempt } from '@/services/diagnostics/mediaAttemptRuntime';
import { captureSentryExceptionWithScope } from '@/services/monitoring/sentry';
import { createMediaDiagnosticRecorder } from '@/services/diagnostics/mediaDiagnosticRuntime';
import React, { useEffect, useState, useRef } from 'react';
import { View, Image, StyleSheet, TouchableOpacity, ActivityIndicator, Text, Platform, Alert, Modal } from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import * as FileSystem from 'expo-file-system/legacy';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import theme from '../../config/theme';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { useWebAlert, AlertButton } from '../WebAlert';
import { uploadChecklistSignature, deleteChecklistSignature } from '../../services/checklist/ChecklistImageService';
import { databaseService } from '../../services/databaseService';
import { APP_CONSTANTS } from '../../config/constants';
import { v4 as uuidv4 } from 'uuid';
import { debugPause } from '@/utils/debugPauseUniversal';
import { ChecklistPhotoFieldLogger } from '@/utils/logger';
import SignaturePad from './SignaturePad';
import { traceImage } from '@/utils/debugImageTrace';

// Tipagem das props do campo de foto do checklist
interface ChecklistSignatureFieldProps {
    empresaId: string;
    checklistId: string;
    fieldId: string;
    value?: string;
    onChange: (uri: string) => void;
}

/**
 * Campo de foto para checklist
 * Permite tirar foto ou escolher da galeria, faz upload local-first e atualiza o draft
 */
const ChecklistSignatureField: React.FC<ChecklistSignatureFieldProps> = ({
    empresaId,
    checklistId,
    fieldId,
    value,
    onChange,
}) => {
    const isIosPad = Platform.OS === 'ios' && Platform.isPad;
    const [modalOpen, setModalOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    // WEB: quando o valor do campo é um ID local (IndexedDB), resolvemos para data URL
    const resolvedUri = useChecklistMediaPreview(value, APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_SIGNATURES.NAME, 'image/png', { companyId: empresaId, checklistId, fieldId });
    const retainedPreview = useRetainedLocalMediaPreview(resolvedUri, `${empresaId}:${checklistId}:${fieldId}`);
    const uriToShow = retainedPreview.visibleUri;
    const { showActionSheetWithOptions } = useActionSheet();
    const { showAlert } = useWebAlert();
    useEffect(() => subscribeResumedChecklistMedia(event => {
        if (event.checklistId === checklistId && event.fieldId === fieldId &&
            ((value ?? '') === event.expectedValue || value === event.localUri)) onChange(event.uri);
    }), [checklistId, fieldId, value, onChange]);
    const shouldShowImageSource = APP_CONSTANTS.FEATURE_FLAGS?.SHOW_DEBUG_ACTIONS === true &&
        APP_CONSTANTS.DEBUG?.SHOW_IMAGE_SOURCE === true;

    // Função para mostrar alerta adaptável à plataforma
    const showMessage = (
        title: string,
        msg: string,
        buttons?: AlertButton[]
    ) => {
        if (Platform.OS !== 'android') {
            showAlert(title, msg, buttons ?? [{ text: 'OK' }]);
        } else {
            Alert.alert(title, msg, buttons ?? [{ text: 'OK' }]);
        }
    };

    const drawingSession = useRef<string | null>(null);
    const onDrawingEvent = (phase: 'drawing_started' | 'drawing_cleared' | 'drawing_empty' | 'drawing_cancelled') => {
        const id = drawingSession.current ??= uuidv4();
        createMediaDiagnosticRecorder({ checklistId, fieldId, attemptId: id })(phase);
    };
    const saveAttempt = useRef<string | null>(null);
    const onSaveStart = async () => {
        const id = saveAttempt.current ?? drawingSession.current ?? uuidv4();
        saveAttempt.current = id;
        await beginMediaAttempt({ id, checklistId, fieldId, source: 'signature' });
        createMediaDiagnosticRecorder({ checklistId, fieldId, attemptId: id })('save_requested');
    };
    const onSaveCancel = () => {
        if (saveAttempt.current) finishMediaAttempt(saveAttempt.current);
        saveAttempt.current = null;
    };
    const onSaveError = (error: unknown) => {
        if (saveAttempt.current) finishMediaAttempt(saveAttempt.current, true, { phase: 'capture_failed', errorCode: mediaErrorCode(error) });
        saveAttempt.current = null;
    };
    // Faz o upload da imagem e atualiza o draft
    const handleSelectedImage = async (dataUrl: string) => {
        const mediaAttemptId = saveAttempt.current ?? uuidv4();
        saveAttempt.current = null;
        drawingSession.current = null;
        await beginMediaAttempt({ id: mediaAttemptId, checklistId, fieldId, source: 'signature' });
        const recordMedia = createMediaDiagnosticRecorder({ checklistId, fieldId, attemptId: mediaAttemptId });
        recordMedia('capture_received');
        ChecklistPhotoFieldLogger.Main.info('[handleSelectedImage] Iniciando seleção de assinatura');
        setSaving(true);
        setModalOpen(false); // Fecha o modal imediatamente
        try {
            let localUri = dataUrl;
            
            // Processamento local da assinatura (web e mobile)
            if (Platform.OS === 'web') {
                // Web: Ler a imagem como base64 e salvar na tabela CHECKLIST_SIGNATURES
                const response = await fetch(dataUrl);
                const blob = await response.blob();
                const reader = new FileReader();
                const base64 = await new Promise<string>((resolve, reject) => {
                    reader.onerror = () => reject(reader.error);
                    reader.onload = () => resolve((reader.result as string).split(',')[1]);
                    reader.readAsDataURL(blob);
                });
                // Inferir extensão pelo prefixo base64
                let extension = 'png'; // Padrão para assinaturas
                if (base64.startsWith('iVBORw0KGgo')) {
                    extension = 'png';
                } else if (base64.startsWith('R0lGOD')) {
                    extension = 'gif';
                } else if (base64.startsWith('UklGR')) {
                    extension = 'webp';
                }
                ChecklistPhotoFieldLogger.Main.info(`[handleSelectedImage] Extensão inferida: ${extension}`);
                // Gerar id único para a imagem, agora com extensão
                const destPath = `checklist-signatures/${empresaId}/${checklistId}-${fieldId}-${Date.now()}-${mediaAttemptId}.${extension}`;
                ChecklistPhotoFieldLogger.Main.info('[handleSelectedImage] Salvando imagem no IndexedDB');
                // Salvar na tabela CHECKLIST_SIGNATURES
                await databaseService.createItem(
                    APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_SIGNATURES.NAME,
                    {
                        id: destPath,
                        base64,
                        created_at: Date.now(),
                        updated_at: Date.now(),
                    },
                    true // skipPendingOperation
                );
                // Deletar a URI temporária (blob:...)
                try {
                    ChecklistPhotoFieldLogger.Main.info('[handleSelectedImage] Deletando URI temporária');
                    console.log('Deletando a URI temporária (blob:...) = ' + dataUrl);
                    await debugPause('Deletando a URI temporária (blob:...) = ' + dataUrl);
                    URL.revokeObjectURL(dataUrl);
                    console.log('URI temporária deletada = ' + dataUrl);
                    await debugPause('URI temporária deletada = ' + dataUrl);
                } catch { }
                localUri = destPath;
            } else {
                // Mobile: Extrair base64 do dataURL e salvar como arquivo físico
                const base64Data = dataUrl.split(',')[1]; // Remove o prefixo "data:image/png;base64,"
                
                // Gerar caminho único para o arquivo
                const timestamp = Date.now();
                const fileName = `${checklistId}-${fieldId}-${timestamp}-${mediaAttemptId}.png`;
                const destPath = `checklist-signatures/${empresaId}/${fileName}`;
                
                ChecklistPhotoFieldLogger.Main.info('[handleSelectedImage] Mobile: Salvando assinatura como arquivo');
                
                // Salvar o arquivo no sistema de arquivos usando FileSystem
                const fileUri = `${FileSystem.documentDirectory}${destPath}`;
                
                // Criar diretório se necessário
                const dirPath = `${FileSystem.documentDirectory}checklist-signatures/${empresaId}/`;
                const dirInfo = await FileSystem.getInfoAsync(dirPath);
                if (!dirInfo.exists) {
                    await FileSystem.makeDirectoryAsync(dirPath, { intermediates: true });
                }
                
                // Escrever o arquivo
                await FileSystem.writeAsStringAsync(fileUri, base64Data, {
                    encoding: FileSystem.EncodingType.Base64
                });
                
                ChecklistPhotoFieldLogger.Main.info('[handleSelectedImage] Mobile: Arquivo salvo localmente');
                localUri = fileUri;
                await debugPause('SignatureField handleSelectedImage - arquivo local salvo');
            }
            
            ChecklistPhotoFieldLogger.Main.info('[handleSelectedImage] Chamando uploadChecklistSignature');
            
            recordMedia('local_file_ready');
            const result = await uploadChecklistSignature(checklistId, fieldId, localUri, onChange, mediaAttemptId, value ?? '');
            if (result.superseded) return;
            if (result.success && result.data?.uri) {
                ChecklistPhotoFieldLogger.Main.info(`[handleSelectedImage] ${result.remoteAcknowledged === true ? 'Mídia enviada; referência remota confirmada.' : 'Mídia salva localmente; envio pendente.'}`);
                onChange(result.data.uri); // Atualiza o draft com a nova URI/URL
                showChecklistMediaFeedback(result, 'Assinatura', showMessage, onChange);
            } else {
                ChecklistPhotoFieldLogger.Main.info(`[handleSelectedImage] Upload falhou ou parcial. Erro: ${result.error}`);
                showChecklistMediaFeedback(result, 'Assinatura', showMessage, onChange);
            }
        } catch (err) {
            recordMedia('local_file_failed', err);
            captureSentryExceptionWithScope(err, {
                context: { flow: 'checklist_media', stage: 'local_file', checklistId, campoId: fieldId, attemptId: mediaAttemptId },
                tags: { flow: 'upload', stage: 'local_file' },
            });
            ChecklistPhotoFieldLogger.Main.info(`[handleSelectedImage] Exceção lançada: ${err}`);
            showChecklistMediaFeedback({ success: false }, 'Assinatura', showMessage, onChange);
        } finally {
            setSaving(false);
            ChecklistPhotoFieldLogger.Main.info('[handleSelectedImage] Finalizado');
        }
    };

    // Função para confirmar e remover a assinatura
    const handleRemoveSignature = () => {
        const confirmRemove = () => {
            showMessage(
                'Confirmar remoção',
                'Tem certeza que deseja remover esta assinatura?',
                [
                    { text: 'Cancelar', style: 'cancel' },
                    { text: 'Remover', style: 'destructive', onPress: performRemove }
                ]
            );
        };

        const performRemove = async () => {
            ChecklistPhotoFieldLogger.Main.info(`[handleRemoveSignature] Iniciando remoção para fieldId=${fieldId}`);
            setDeleting(true);
            try {
                const result = await deleteChecklistSignature(checklistId, fieldId);
                if (result.success) {
                    ChecklistPhotoFieldLogger.Main.info(`[handleRemoveSignature] Remoção concluída com sucesso`);
                    onChange(''); // Limpa o valor no draft
                    showMessage('Sucesso', 'Assinatura removida com sucesso!');
                } else {
                    ChecklistPhotoFieldLogger.Main.info(`[handleRemoveSignature] Remoção falhou: ${result.error}`);
                    showMessage('Erro', result.error || 'Falha ao remover a assinatura.');
                }
            } catch (err) {
                ChecklistPhotoFieldLogger.Main.info(`[handleRemoveSignature] Exceção lançada: ${err}`);
                showMessage('Erro', 'Falha ao remover a assinatura.');
            } finally {
                setDeleting(false);
                ChecklistPhotoFieldLogger.Main.info(`[handleRemoveSignature] Finalizado para fieldId=${fieldId}`);
            }
        };

        confirmRemove();
    };

    // Renderização do campo de foto/assinatura (usa resolvedUri no web quando offline)
    return (
        <View style={styles.container}>
            <TouchableOpacity
                style={value ? styles.touchWithImage : styles.touchEmpty}
                onPress={value ? undefined : () => setModalOpen(true)}
                disabled={!!value}
            >
                {uriToShow
                    ? <>
                        {retainedPreview.pendingUri && <Image
                            key={retainedPreview.visibleGeneration}
                            source={{ uri: uriToShow }}
                            style={[styles.thumb, StyleSheet.absoluteFill]}
                            resizeMode="contain"
                        />}
                        <Image
                            key={retainedPreview.generation}
                            source={{ uri: resolvedUri! }}
                            style={[styles.thumb, retainedPreview.pending && { opacity: 0 }]}
                            resizeMode="contain"
                            onLoad={retainedPreview.onLoad}
                            onError={retainedPreview.onError}
                        />
                    </>
                    : <Ionicons name="pencil-outline" size={28} color={theme.colors.primary.default} />}
            </TouchableOpacity>
            {retainedPreview.failed && <ChecklistImageLoadNotice uri={resolvedUri}
                onRetry={retainedPreview.retry} testID={`signature-load-status-${fieldId}`} />}
            {shouldShowImageSource && uriToShow && (
              <Text style={{ fontSize: 11, color: '#666', marginTop: 4 }} numberOfLines={2}>
                {`src: ${uriToShow.slice(0, 120)}`}
              </Text>
            )}

            {/* Botões de ação - só aparecem quando há uma assinatura */}
            {value && (
                <View style={styles.actionButtons}>
                    <TouchableOpacity
                        style={styles.actionButton}
                        onPress={() => setModalOpen(true)}
                        disabled={saving || deleting}
                    >
                        {saving ? (
                            <ActivityIndicator size="small" color={theme.colors.primary.default} />
                        ) : (
                            <Text style={[styles.actionButtonText, { color: theme.colors.primary.default }]}>
                                Alterar
                            </Text>
                        )}
                    </TouchableOpacity>

                    <TouchableOpacity
                        style={[styles.actionButton, styles.removeButton]}
                        onPress={handleRemoveSignature}
                        disabled={deleting || saving}
                    >
                        {deleting ? (
                            <ActivityIndicator size="small" color={theme.colors.util.error} />
                        ) : (
                            <Text style={[styles.actionButtonText, { color: theme.colors.util.error }]}>
                                Remover
                            </Text>
                        )}
                    </TouchableOpacity>
                </View>
            )}

            {/* Modal de assinatura */}
            {modalOpen && (
                <Modal
                    visible
                    onRequestClose={() => { onSaveCancel(); setModalOpen(false); }}
                    animationType="slide"
                    presentationStyle={isIosPad ? 'fullScreen' : 'formSheet'}
                    supportedOrientations={['portrait', 'landscape']}
                >
                    <SignaturePad onOK={handleSelectedImage} onDrawingEvent={onDrawingEvent} onSaveStart={onSaveStart} onSaveCancel={onSaveCancel} onSaveError={onSaveError} onClose={() => { onDrawingEvent('drawing_cancelled'); onSaveCancel(); drawingSession.current = null; setModalOpen(false); }} />
                </Modal>
            )}
        </View>
    );
}

// Estilos seguindo o padrão do theme
const styles = StyleSheet.create({
    container: {
        // Removido marginVertical pois agora o componente está dentro de itemContainer
    },
    label: {
        fontSize: theme.fontSizes.sm,
        color: theme.colors.text.secondary,
        marginBottom: theme.spacing.xs,
    },
    touchEmpty: {
        width: '100%',
        height: 135,
        borderRadius: theme.borderRadius.sm,
        backgroundColor: theme.colors.background.secondary,
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: theme.colors.border.light,
        ...theme.shadows.sm,
    },
    touchWithImage: {
        width: '100%',
        height: 135,
        borderRadius: theme.borderRadius.sm,
        backgroundColor: theme.colors.background.secondary,
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: theme.colors.border.light,
        ...theme.shadows.sm,
    },
    button: {
        borderWidth: 1,
        borderColor: theme.colors.primary.default,
        borderRadius: theme.borderRadius.sm,
        paddingHorizontal: theme.spacing.sm,
        paddingVertical: theme.spacing.xs,
    },
    buttonText: {
        color: theme.colors.primary.default,
        fontSize: theme.fontSizes.xs,
        fontWeight: theme.fontWeights.medium as any,
    },
    thumb: {
        width: '100%',
        height: '100%',
        borderRadius: theme.borderRadius.sm,
    },
    actionButtons: {
        flexDirection: 'row',
        justifyContent: 'flex-start',
        marginTop: theme.spacing.sm,
        gap: theme.spacing.sm,
    },
    actionButton: {
        borderWidth: 1,
        borderColor: theme.colors.primary.default,
        borderRadius: theme.borderRadius.sm,
        paddingHorizontal: theme.spacing.sm,
        paddingVertical: theme.spacing.xs,
    },
    actionButtonText: {
        fontSize: theme.fontSizes.xs,
        fontWeight: theme.fontWeights.medium as any,
    },
    removeButton: {
        borderColor: theme.colors.util.error,
    },
});

export default ChecklistSignatureField;
